import { ComponentProps, useEffect, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import ModalContentWithControl from "../components/ModalContentWithControl";
import EntityMediaOverview from "../../entity-media-overview/EntityMediaOverview";
import { usePopupActions } from "../../../store/popupStore";
import useEntityMapModalController from "../../../hooks/useEntityMapModalController";
import { useConfig } from "../../../contexts/ConfigContext";
import { useSelectedEntity } from "../../../hooks/useSelectedEntity";
import { hasValidGeometry } from "../../../utilities/geoUtils";

export function EntityMediaModal(props: ComponentProps<typeof ModalContentWithControl>) {
  return (
    <ModalContentWithControl
      {...props}
      containerSx={{ maxHeight: "100%", padding: 0, position: "relative", zIndex: -1, ...props?.containerSx }}
      contentSx={{
        maxHeight: "inherit",
        padding: 0,
        position: "absolute",
        top: 0,
        width: "100%",
        left: 0,
        height: "100%",
        ...props.contentSx
      }}
    />
  );
}
export function EntityMediaContent() {
  const { entity, isFetchingRelationships, fetchRelationships } = useSelectedEntity();
  const { toggleContent, closePopup, closeFullMediaViewer } = usePopupActions();
  const config = useConfig();
  const hasAutoCollapsedRef = useRef(false);

  async function handleFetchRelationships() {
    const hasFoundRelation = await fetchRelationships();
    if (hasFoundRelation) {
      // Collapse popup content to reveal the map with related entity markers
      toggleContent();
    } else {
      enqueueSnackbar("No related items found", { variant: "info" });
    }
  }

  // Collapse modal content once when the entity first loads (auto-relationships mode).
  // Use a ref guard so subsequent re-renders (e.g. isFetchingRelationships flip) don't
  // re-toggle the panel back open.
  useEffect(() => {
    if (!config?.alwaysRequestRelatedItemsAndMedia || !entity || isFetchingRelationships) {
      return;
    }
    if (hasAutoCollapsedRef.current) return;
    hasAutoCollapsedRef.current = true;
    toggleContent();
  }, [config?.alwaysRequestRelatedItemsAndMedia, entity, isFetchingRelationships, toggleContent]);

  if (!entity) return null;

  // Determine the correct close action for embedded media.
  // For geo-tagged entities the popup switches back to map view; for others it closes entirely.
  const handleCloseEmbed = hasValidGeometry(entity) ? closeFullMediaViewer : closePopup;

  return (
    <EntityMediaOverview
      key={entity.id}
      entity={entity}
      mode='expanded'
      onFindRelatedItem={handleFetchRelationships}
      isLoading={isFetchingRelationships}
      mediaPreviewOptions={{ onClose: handleCloseEmbed }}
    />
  );
}

export default function MediaEntityMapModal() {
  const { showModal } = useEntityMapModalController();
  const { entity } = useSelectedEntity();
  const { closePopup, closeFullMediaViewer } = usePopupActions();

  const isGeoTagged = entity ? hasValidGeometry(entity) : false;
  const handleClose = isGeoTagged ? closeFullMediaViewer : closePopup;

  // Hide the outer ModalControl X when an embed is showing — EmbedRenderer
  // renders its own collapsible close button that sits above the iframe.
  const entityAny = entity as any;
  const isEmbed =
    entityAny?.media_type?.startsWith("embed") || Boolean(entityAny?.embed_url || entityAny?.remote_details?.embed_url) || false;

  return (
    <EntityMediaModal open={showModal} showContent={true} hideToggleButton={true} onClose={handleClose} hideControl={isEmbed}>
      <EntityMediaContent />
    </EntityMediaModal>
  );
}
